import { ProfileChipContainer } from 'components/Chips/ChipContainers';
import MenteeStatusChip from 'components/Chips/MenteeStatusChip';
import RoleChip from 'components/Chips/RoleChip';
import { DefaultLoader, LoaderContainer } from 'components/Loader';
import {
  HeadButtons,
  PageBack,
  PageHeadContainer,
  PageTitle,
} from 'components/PageHead';
import ProfileNavigation from 'components/profileNavigation';
import ProfileLink from 'components/profileNavigation/Link';
import { MenteeStatusValues } from 'enums/MenteeStatus';
import { RoleValues } from 'enums/Roles';
import React, { FC, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import useSWR from 'swr';
import User from 'types/User';
import MenteeButtonRoutes from './ButtonRoutes';
import MenteeProfileRoutes from './Routes';

type ContextType = {
  mentee: User | undefined;
};
export const MenteeEditContext = React.createContext({} as ContextType);
const { Provider } = MenteeEditContext;

const MenteeEdit: FC = ({ children }) => {
  const { id } = useParams();

  const { data: mentee, error } = useSWR<User>(`/user/${id}`);

  const value = useMemo(
    () => ({
      mentee,
    }),
    [mentee],
  );

  return error ? (
    <p>L'utilisateur n'existe pas.</p>
  ) : mentee ? (
    <Provider value={value}>{children}</Provider>
  ) : (
    <LoaderContainer>
      <DefaultLoader size={1.5} />
    </LoaderContainer>
  );
};

const PageBackContainer = styled.div`
  margin-bottom: auto;
  font-size: 1.5rem;
`;
const TitleContainer = styled.div``;

const NameEmail = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 0.5rem;
`;
const Email = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  color: var(--gray-500);
  div:first-child {
    margin-right: 0.8rem;
  }
`;

const Head: FC = () => {
  const { mentee } = useContext(MenteeEditContext);
  return (
    <PageHeadContainer>
      <PageTitle>
        <PageBackContainer>
          <PageBack href="/admin/mentoring/mentees" />
        </PageBackContainer>
        <TitleContainer>
          <NameEmail>
            <h1>{mentee?.firstName + ' ' + mentee?.lastName}</h1>
            <Email>
              <div>&bull;</div>
              {mentee?.email}
            </Email>
          </NameEmail>
          <ProfileChipContainer>
            {mentee?.UserRole?.map(
              (role: { role: RoleValues }, idx: number) => (
                <RoleChip role={role.role} key={idx} />
              ),
            )}
            {mentee?.menteeStatus && mentee.MenteeMentorship && (
              <MenteeStatusChip
                status={mentee.menteeStatus}
                mentorshipStatus={
                  mentee.MenteeMentorship[0]?.status || 'ONGOING'
                }
              />
            )}
          </ProfileChipContainer>
        </TitleContainer>
      </PageTitle>
      <HeadButtons>
        <MenteeButtonRoutes menteeId={mentee?.id} />
        {/*
				DISABLED
				<Button color="primary" icon={<ChevronDownIcon />} iconSide='right'>
					Actions
				</Button> */}
      </HeadButtons>
    </PageHeadContainer>
  );
};

const Navigation: FC = () => {
  const { mentee } = useContext(MenteeEditContext);
  const baseURL = useMemo(() => {
    return `/admin/mentoring/mentees/${mentee?.id}/`;
  }, [mentee]);
  return (
    <ProfileNavigation>
      <ProfileLink to={baseURL}>Informations générales</ProfileLink>
      <ProfileLink to={baseURL + 'form'}>Formulaire mentoré</ProfileLink>
      {(
        ['ELIGIBLE', 'MENTORSHIP_ACTIVE', 'REFUSED'] as MenteeStatusValues[]
      ).includes(mentee?.menteeStatus as MenteeStatusValues) && (
        <ProfileLink
          to={baseURL + 'mentoring/'}
          activeLink={baseURL + 'mentoring'}
        >
          Mentorat
        </ProfileLink>
      )}

      {mentee?.menteeStatus === 'MENTORSHIP_ACTIVE' && (
        <ProfileLink to={baseURL + 'interviews/'}>
          Entretiens programmés
        </ProfileLink>
      )}
      {/*
			DISABLED
			<ProfileLink to={baseURL + 'tools'}>Outils</ProfileLink>
			<ProfileLink to={baseURL + 'transaction-history'}>Historiques transactions</ProfileLink> */}
    </ProfileNavigation>
  );
};

const Usage: FC = (props) => {
  return (
    <MenteeEdit {...props}>
      <Head />
      <Navigation />
      <MenteeProfileRoutes />
    </MenteeEdit>
  );
};

export default Usage;
